import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { 
  FiInbox, FiSearch, FiLogOut, FiUser, FiLoader, 
  FiRefreshCcw, FiAlertCircle, FiShield, FiCalendar, FiExternalLink
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { leadsApi, authApi } from '../services/api';
import LeadTable from '../components/LeadTable';
import LeadCard from '../components/LeadCard';
import LeadDetails from '../components/LeadDetails';

export default function Leads() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const fetchLeads = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    
    try {
      const response = await leadsApi.getLeads(
        user.client_key, 
        user.email, 
        user.session_token
      );
      
      if (response.success) {
        setLeads(response.leads || []);
      } else {
        setError(response.message || 'Failed to load leads.');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) fetchLeads();
  }, [user]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await authApi.logout(user.session_token);
    } catch (e) {
      console.warn("Logout notification failed", e);
    } finally {
      logout();
    }
  };

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const searchStr = `${lead.name} ${lead.email} ${lead.phone} ${lead.message}`.toLowerCase();
      const matchesSearch = searchStr.includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      if (dateFilter === 'all') return true;
      
      const leadDate = new Date(lead.created_at);
      const now = new Date();
      
      if (dateFilter === 'today') return leadDate.toDateString() === now.toDateString();
      if (dateFilter === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(now.getDate() - 7);
        return leadDate >= weekAgo;
      }
      if (dateFilter === 'month') return leadDate.getMonth() === now.getMonth() && leadDate.getFullYear() === now.getFullYear();
      
      return true;
    });
  }, [leads, searchTerm, dateFilter]);

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setIsDetailOpen(true);
  };

  const statusColors = {
    active: 'bg-emerald-500',
    trial: 'bg-blue-500',
    grace: 'bg-amber-500',
    expired: 'bg-red-500',
    paused: 'bg-slate-500'
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('active')) return statusColors.active;
    if (s.includes('trial')) return statusColors.trial;
    if (s.includes('grace')) return statusColors.grace;
    if (s.includes('expire')) return statusColors.expired;
    if (s.includes('pause')) return statusColors.paused;
    return 'bg-slate-400';
  };

  return (
    <div className="w-full max-w-[1240px] mx-auto flex flex-col gap-6">
      {/* Top Corporate Header Bar */}
      <nav className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-4 px-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg transform -rotate-3">
            <span className="text-navy-900 font-black text-sm tracking-tighter">BPA</span>
          </div>
          <div>
            <h2 className="text-white font-black text-lg leading-none tracking-tight">BPAS Client Lead Portal</h2>
            <p className="text-slate-400 text-[10px] uppercase font-bold tracking-widest mt-1">SaaS Secure Environment</p>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-white/5 backdrop-blur-md p-1.5 pr-4 rounded-2xl border border-white/10">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => fetchLeads(true)} 
              disabled={refreshing || loading}
              className="p-2.5 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-all disabled:opacity-30"
              title="Refresh Data"
            >
              <SafeIcon icon={FiRefreshCcw} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <div className="w-px h-6 bg-white/10"></div>
            <div className="flex flex-col items-end">
              <span className="text-white text-xs font-bold">{user?.email}</span>
              <button 
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="text-[10px] text-red-400 font-black uppercase tracking-widest hover:text-red-300 transition-colors"
              >
                {isLoggingOut ? 'Exiting...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Summary Section */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white rounded-[2rem] p-8 shadow-xl shadow-navy-900/5 flex flex-col justify-between relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-full blur-3xl -mr-32 -mt-32 group-hover:bg-brand-blue/5 transition-colors duration-500"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <span className={`w-2 h-2 rounded-full ${getStatusColor(user?.service_status)} animate-pulse`}></span>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Service Account: {user?.service_status || 'Active'}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight mb-2">
              {user?.business_name || 'My Business'}
            </h1>
            <p className="text-slate-500 text-sm font-medium flex items-center gap-2">
              <SafeIcon icon={FiInbox} /> Website lead enquiries management
            </p>
          </div>
          <div className="relative z-10 mt-8 flex items-center gap-4">
            <a href="mailto:support@bpasolutions.au" className="flex items-center gap-2 text-xs font-bold text-brand-blue hover:underline">
              <SafeIcon icon={FiShield} /> Support Assistance
            </a>
          </div>
        </div>

        <div className="bg-navy-900 rounded-[2rem] p-8 shadow-xl text-white flex flex-col justify-center relative overflow-hidden">
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-brand-cyan/10 rounded-full blur-2xl -mb-16 -mr-16"></div>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">Total Vaulted</p>
          <div className="flex items-baseline gap-2">
            <span className="text-6xl font-black text-white tracking-tighter">{leads.length}</span>
            <span className="text-brand-cyan font-bold text-sm">Leads</span>
          </div>
          <div className="mt-6 flex items-center gap-2 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
            <SafeIcon icon={FiCalendar} />
            Updated {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </section>

      {/* Filter/Action Row */}
      <div className="bg-white rounded-[1.5rem] p-3 shadow-lg shadow-navy-900/5 border border-slate-100 flex flex-col lg:flex-row items-center gap-4">
        <div className="w-full lg:w-auto lg:flex-grow relative group">
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-300 group-focus-within:text-brand-blue transition-colors">
            <SafeIcon icon={FiSearch} className="text-lg" />
          </div>
          <input 
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search leads by name, email or message content..." 
            className="w-full pl-12 pr-6 py-4 bg-slate-50 rounded-2xl text-sm text-navy-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-brand-blue/5 transition-all font-bold border-2 border-transparent focus:border-brand-blue/10"
          />
        </div>

        <div className="w-full lg:w-auto flex items-center gap-1 bg-slate-50 p-1.5 rounded-2xl overflow-x-auto no-scrollbar">
          {[
            { id: 'all', label: 'All Time' },
            { id: 'today', label: 'Today' },
            { id: 'week', label: '7 Days' },
            { id: 'month', label: 'Month' }
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => setDateFilter(filter.id)}
              className={`whitespace-nowrap px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                dateFilter === filter.id 
                  ? 'bg-white text-brand-blue shadow-md' 
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Leads Table/Card Container */}
      <div className="bg-white rounded-[2.5rem] shadow-xl shadow-navy-900/5 border border-slate-100 overflow-hidden flex flex-col min-h-[500px]">
        {loading ? (
          <div className="flex-grow flex flex-col items-center justify-center p-12">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 border-4 border-slate-50 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-t-brand-blue rounded-full animate-spin"></div>
            </div>
            <p className="mt-8 text-slate-400 font-black uppercase tracking-[0.3em] text-[10px]">Authorizing Secure View</p>
          </div>
        ) : error ? (
          <div className="flex-grow flex flex-col items-center justify-center p-12 text-center">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-6">
              <SafeIcon icon={FiAlertCircle} className="text-4xl" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-2 uppercase tracking-tight">Sync Encountered An Error</h3>
            <p className="text-slate-500 mb-8 max-w-xs mx-auto text-sm font-medium">{error}</p>
            <button 
              onClick={() => fetchLeads()} 
              className="px-10 py-4 bg-navy-900 text-white font-black uppercase tracking-widest text-[10px] rounded-xl hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20"
            >
              Retry Reconnection
            </button>
          </div>
        ) : filteredLeads.length > 0 ? (
          <>
            <div className="hidden md:block">
              <LeadTable leads={filteredLeads} onSelectLead={handleSelectLead} />
            </div>
            <div className="md:hidden p-4 space-y-4">
              {filteredLeads.map(lead => (
                <LeadCard key={lead.id} lead={lead} onClick={() => handleSelectLead(lead)} />
              ))}
            </div>
          </>
        ) : (
          <div className="flex-grow flex flex-col items-center justify-center p-12 text-center">
            <div className="w-24 h-24 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <SafeIcon icon={FiInbox} className="text-4xl text-slate-200" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-3 tracking-tighter uppercase">No Enquiries Found</h3>
            <p className="text-slate-500 max-w-sm mx-auto text-sm font-medium leading-relaxed">
              {leads.length === 0 
                ? "Your lead vault is currently empty. Incoming submissions from your website will appear here automatically."
                : "Your search parameters returned zero results. Try adjusting your filters or search terms."}
            </p>
          </div>
        )}
      </div>

      <LeadDetails lead={selectedLead} isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} />
    </div>
  );
}